import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { X, Plus, Trash2, FolderInput, FolderPlus } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem,
  ContextMenuSub, ContextMenuSubTrigger, ContextMenuSubContent, ContextMenuSeparator, ContextMenuLabel,
} from "@/components/ui/context-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const norm = (t) => (t || "").trim().toLowerCase().replace(/^#+/, "").trim().slice(0, 40);

// Chip-based tag editor with library typeahead + learning AI suggestions.
// adminWorkspace: dense 2-col categories, right-click power menu, hides the redundant suggestions/lit chips.
export const TagInput = ({ value = [], onChange: onChangeProp, mediaId, enableAi = true, placeholder = "type a tag…", testid = "tag-input", adminWorkspace = false, refreshSignal = 0, hideCategories = [], hideTags = [] }) => {
  const [input, setInput] = useState("");
  const [ac, setAc] = useState([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const [cats, setCats] = useState([]);
  const catsRef = useRef([]);
  useEffect(() => { catsRef.current = cats; }, [cats]);
  const onChange = (next) => {
    const meta = {};
    for (const c of catsRef.current || []) for (const t of (c.tags || [])) meta[t] = c.category;
    onChangeProp(next, meta);   // ONE registry: chip → canonical tag + category → Save
  };
  const [newCatFor, setNewCatFor] = useState(null); // tag being moved to a brand-new category
  const [newCatName, setNewCatName] = useState("");
  const [makeCatOpen, setMakeCatOpen] = useState(false); // standalone "＋ New category" flow
  const [makeCatName, setMakeCatName] = useState("");
  const [makeCatTag, setMakeCatTag] = useState("");
  const [addingCat, setAddingCat] = useState(null); // category name we're adding a tag to
  const [addTagVal, setAddTagVal] = useState("");
  const reloadCats = (retries = 2) => api.get("/tags/categories")
    .then((r) => setCats((r.data || []).filter((c) => c.category !== "Aspect Ratio")))
    .catch(() => { if (retries > 0) setTimeout(() => reloadCats(retries - 1), 700); });
  useEffect(() => { if (enableAi) reloadCats(); }, [enableAi]);
  const debRef = useRef(null);

  const taxSet = new Set(cats.flatMap((c) => c.tags));

  // Free-text is NOT a tag. A typed term must already be canonical/alias (autocomplete) — otherwise the owner gets an
  // explicit "Create new tag" step (category required). Nothing unknown is ever pushed into manual_tags.
  const [createFor, setCreateFor] = useState(null); // term awaiting explicit canonical creation
  const [createCat, setCreateCat] = useState("");
  const [creating, setCreating] = useState(false);
  const addTag = (t, { fromPicker = false } = {}) => {
    const n = norm(t);
    if (!n) { setInput(""); return; }
    if (!fromPicker && !taxSet.has(n) && !ac.includes(n)) {
      setInput(""); setAc([]); setOpen(false); setHi(0);
      if (adminWorkspace) { setCreateFor(n); setCreateCat(""); }
      else toast.error(`"${n}" isn't in the taxonomy — pick an existing tag`);
      return;
    }
    if (!value.includes(n)) onChange([...value, n]);
    setInput(""); setAc([]); setOpen(false); setHi(0);
  };
  const confirmCreate = async () => {
    if (!createFor || !createCat) return;
    setCreating(true);
    try {
      const r = await api.post("/tags/create", { tag: createFor, category: createCat });
      const canon = r.data.tag;
      await reloadCats();
      if (canon && !value.includes(canon)) onChange([...value, canon]);
      toast.success(r.data.created ? `Created "${canon}" in ${createCat}` : `"${canon}" already exists — selected it`);
      setCreateFor(null); setCreateCat("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create tag"); }
    finally { setCreating(false); }
  };

  // --- admin power actions on a category tag ---
  const suppressTag = async (t) => {
    try {
      const r = await api.post("/tags/suppress", { tag: t });
      onChange(value.filter((x) => x !== t));
      setCats((r.data.categories || cats).filter((c) => c.category !== "Aspect Ratio"));
      const n = r.data.removed_from || 0;
      toast.success(`Removed "${t}" permanently — the AI won't suggest it again` + (n ? ` · cleared from ${n} photo${n === 1 ? "" : "s"}` : ""));
    } catch { toast.error("Couldn't remove that tag"); }
  };
  const moveTag = async (t, category) => {
    try {
      const r = await api.post("/tags/master/move", { tag: t, category });
      setCats((r.data.categories || cats).filter((c) => c.category !== "Aspect Ratio"));
      toast.success(`Moved "${t}" to ${category}`);
    } catch { toast.error("Couldn't move that tag"); }
  };
  const submitNewCat = async () => {
    const name = newCatName.trim();
    if (!name || !newCatFor) { setNewCatFor(null); setNewCatName(""); return; }
    await moveTag(newCatFor, name);
    setNewCatFor(null); setNewCatName("");
  };
  const createCategory = async () => {
    const name = makeCatName.trim();
    const tag = makeCatTag.trim().toLowerCase();
    if (!name || !tag) { toast.error("Add a category name and a first tag"); return; }
    await moveTag(tag, name);
    setMakeCatOpen(false); setMakeCatName(""); setMakeCatTag("");
  };
  // '+ Add tag' from inside a category in the media editor: creates the canonical tag (dedup) and selects it now.
  const createTagInCat = async (category) => {
    const raw = addTagVal.trim();
    if (!raw) { setAddingCat(null); setAddTagVal(""); return; }
    try {
      const r = await api.post("/tags/create", { category, tag: raw });
      const canon = r.data.tag;
      if (canon && !value.includes(canon)) onChange([...value, canon]); // select immediately (owner-locked)
      await reloadCats();
      if (r.data.created) toast.success(`Added "${canon}" to ${category} and this item`);
      else if (r.data.restored) toast.success(`Restored "${canon}" and selected it`);
      else if (r.data.input && canon !== r.data.input) toast.warning(`"${r.data.input}" resolves to the existing canonical "${canon}" (${r.data.resolved_via} match) — selected "${canon}"; nothing new was created`, { duration: 10000 });
      else toast.success(`"${canon}" already exists — selected it`);
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = typeof det === "object" && det ? (det.message || JSON.stringify(det)) : (det || e?.message || "no response");
      toast.error(`Couldn't add "${raw}" to ${category} — HTTP ${e?.response?.status ?? "network"}: ${msg}`, { duration: 12000 });
      try { await reloadCats(); } catch {}   // the record may still exist server-side — never leave a stale picker
    }
    setAddingCat(null); setAddTagVal("");
  };

  const removeTag = (t) => {
    onChange(value.filter((x) => x !== t));
    if (mediaId && adminWorkspace) {
      toast(`Removed "${t}" from this photo`, {
        action: { label: "Remove permanently", onClick: () => suppressTag(t) },
      });
    }
  };

  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    const q = input.trim();
    if (!q) { setAc([]); setOpen(false); return; }
    debRef.current = setTimeout(async () => {
      try {
        const r = await api.get("/tags/autocomplete", { params: { q } });
        const list = (r.data || []).map((x) => x.tag).filter((t) => !value.includes(t));
        setAc(list); setOpen(list.length > 0); setHi(0);
      } catch {}
    }, 160);
    return () => debRef.current && clearTimeout(debRef.current);
  }, [input, value]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (refreshSignal) reloadCats(); }, [refreshSignal]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (open && ac[hi]) addTag(ac[hi], { fromPicker: true }); else addTag(input);
    } else if (e.key === "ArrowDown" && open) { e.preventDefault(); setHi((h) => Math.min(h + 1, ac.length - 1)); }
    else if (e.key === "ArrowUp" && open) { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Backspace" && !input && chips.length) { removeTag(chips[chips.length - 1]); }
    else if (e.key === "Escape") setOpen(false);
  };

  // In the admin workspace the taxonomy tags are shown lit-up in the categories below,
  // so the top chip box only surfaces custom/off-taxonomy tags (nothing gets orphaned).
  const chips = adminWorkspace ? value.filter((t) => !taxSet.has(t)) : value;

  const CatTag = ({ t, currentCat, single, catTags }) => {
    const on = value.includes(t);
    // Owner-only taxonomy: a tag is either selected by the owner or not. Multi-select everywhere — clicking never
    // deselects a sibling (a scene can contain several people with different characteristics).
    const onClick = () => { if (on) onChange(value.filter((x) => x !== t)); else addTag(t, { fromPicker: true }); };
    const cls = on
      ? "bg-primary text-primary-foreground border-primary font-medium"
      : "bg-secondary/40 border-border text-muted-foreground hover:text-foreground";
    const btn = (
      <button type="button" data-testid={`catpick-${t}`} aria-pressed={on} onClick={onClick}
        className={`text-[11px] rounded-full px-2 py-0.5 border transition-colors inline-flex items-center gap-1 ${cls}`}>
        {t}
      </button>
    );
    if (!adminWorkspace) return btn;
    return (
      <ContextMenu>
        <ContextMenuTrigger asChild>{btn}</ContextMenuTrigger>
        <ContextMenuContent className="w-56">
          <ContextMenuLabel className="truncate">{t}</ContextMenuLabel>
          <ContextMenuSeparator />
          <ContextMenuItem data-testid={`ctx-suppress-${t}`} onSelect={() => suppressTag(t)} className="text-destructive focus:text-destructive">
            <Trash2 className="w-3.5 h-3.5 mr-2" /> Remove permanently
          </ContextMenuItem>
          <ContextMenuSub>
            <ContextMenuSubTrigger data-testid={`ctx-move-${t}`}><FolderInput className="w-3.5 h-3.5 mr-2" /> Move to category</ContextMenuSubTrigger>
            <ContextMenuSubContent className="max-h-72 overflow-y-auto">
              {cats.filter((c) => c.category !== currentCat).map((c) => (
                <ContextMenuItem key={c.category} data-testid={`ctx-move-${t}-to-${c.category}`} onSelect={() => moveTag(t, c.category)}>
                  {c.category}
                </ContextMenuItem>
              ))}
              <ContextMenuSeparator />
              <ContextMenuItem data-testid={`ctx-newcat-${t}`} onSelect={() => { setNewCatFor(t); setNewCatName(""); }}>
                <FolderPlus className="w-3.5 h-3.5 mr-2" /> New category…
              </ContextMenuItem>
            </ContextMenuSubContent>
          </ContextMenuSub>
        </ContextMenuContent>
      </ContextMenu>
    );
  };

  return (
    <div data-testid={testid}>
      <div className="rounded-lg bg-secondary/50 border border-border px-2 py-1.5 flex flex-wrap gap-1.5 items-center focus-within:border-primary/60 transition-colors">
        {chips.map((t) => (
          <span key={t} data-testid={`tagchip-${t}`} className="inline-flex items-center gap-1 text-[12px] rounded-full bg-primary/15 border border-primary/30 text-primary px-2 py-0.5">
            {t}
            <button type="button" onClick={() => removeTag(t)} data-testid={`tagchip-remove-${t}`} className="opacity-60 hover:opacity-100"><X className="w-3 h-3" /></button>
          </span>
        ))}
        <div className="relative flex-1 min-w-[110px]">
          <input data-testid={`${testid}-field`} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown}
            onFocus={() => ac.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 120)}
            placeholder={adminWorkspace ? "type a custom tag…" : placeholder} className="w-full bg-transparent text-sm outline-none px-1 py-0.5" />
          {open && (
            <div className="absolute z-[90] top-full mt-1 left-0 min-w-[180px] bg-popover border border-border rounded-lg overflow-hidden shadow-xl max-h-56 overflow-y-auto">
              {ac.map((t, i) => (
                <button type="button" key={t} data-testid={`tag-ac-${t}`} onMouseDown={(e) => { e.preventDefault(); addTag(t, { fromPicker: true }); }}
                  className={cn("w-full text-left px-3 py-1.5 text-sm flex items-center gap-2", i === hi ? "bg-secondary" : "hover:bg-secondary/60")}>
                  <Plus className="w-3 h-3 text-primary" /> {t}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {enableAi && mediaId && cats.length > 0 && (
        <div className="mt-3 space-y-2 border-t border-border pt-3" data-testid="tag-categories">
          <div className="text-[10px] uppercase tracking-widest text-primary flex items-center gap-1.5 justify-between">
            <span>Tap to tag this photo{adminWorkspace && <span className="normal-case tracking-normal text-muted-foreground/70"> · right-click a tag to remove, move or recategorize</span>}</span>
            {adminWorkspace && (
              <button type="button" data-testid="workspace-new-category" onClick={() => { setMakeCatOpen(true); setMakeCatName(""); setMakeCatTag(""); }}
                className="inline-flex items-center gap-1 shrink-0 normal-case tracking-normal rounded-full border border-primary/40 text-primary px-2 py-0.5 hover:bg-primary/10">
                <FolderPlus className="w-3 h-3" /> New category
              </button>
            )}
          </div>
          <div className={adminWorkspace ? "columns-2 lg:columns-3 xl:columns-4 gap-x-6" : "space-y-2"}>
            {cats.filter((c) => !hideCategories.includes(c.category)).map((c) => (
              <div key={c.category} className={adminWorkspace ? "break-inside-avoid mb-3" : ""}>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1.5">
                  {c.category}
                  {c.single && <span className="text-[8px] normal-case tracking-normal rounded-full bg-primary/15 text-primary px-1.5 py-px border border-primary/30">one</span>}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {c.tags.filter((t) => !hideTags.includes(t) || value.includes(t)).map((t) => <CatTag key={t} t={t} currentCat={c.category} single={c.single} catTags={c.tags} />)}
                  {addingCat === c.category ? (
                    <input autoFocus data-testid={`addtag-input-${c.category}`} value={addTagVal}
                      onChange={(e) => setAddTagVal(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createTagInCat(c.category); } else if (e.key === "Escape") { setAddingCat(null); setAddTagVal(""); } }}
                      onBlur={() => createTagInCat(c.category)} placeholder="new tag…"
                      className="text-[11px] rounded-full px-2 py-0.5 border border-primary/60 bg-secondary/60 outline-none w-24" />
                  ) : (
                    <button type="button" data-testid={`addtag-${c.category}`} onClick={() => { setAddingCat(c.category); setAddTagVal(""); }}
                      className="text-[11px] rounded-full px-2 py-0.5 border border-dashed border-primary/50 text-primary hover:bg-primary/10 inline-flex items-center gap-1">
                      <Plus className="w-2.5 h-2.5" /> Add tag
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!createFor} onOpenChange={(o) => { if (!o) { setCreateFor(null); setCreateCat(""); } }}>
        <DialogContent data-testid="create-tag-dialog" className="max-w-sm bg-card border-border">
          <DialogHeader><DialogTitle className="font-heading text-xl">Create new tag “{createFor}”?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This term isn't in the taxonomy yet. Owner-only: pick its category to register it as a canonical tag. No aliases or plurals are generated.</p>
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Tag</div>
            <div data-testid="create-tag-name" className="rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm">{createFor}</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mt-2">Category <span className="text-primary">*</span></div>
            <select data-testid="create-tag-category" value={createCat} onChange={(e) => setCreateCat(e.target.value)}
              className="w-full h-10 rounded-lg border border-border bg-secondary/40 px-3 text-sm outline-none focus:border-primary/60">
              <option value="">Select a category…</option>
              {cats.map((c) => <option key={c.category} value={c.category}>{c.category}</option>)}
            </select>
          </div>
          <DialogFooter className="gap-2">
            <Button data-testid="create-tag-cancel" type="button" variant="outline" className="rounded-full border-border" onClick={() => { setCreateFor(null); setCreateCat(""); }}>Cancel</Button>
            <Button data-testid="create-tag-confirm" type="button" className="rounded-full" disabled={!createCat || creating} onClick={confirmCreate}>{creating ? "Creating…" : "Create Tag"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={makeCatOpen} onOpenChange={(o) => { if (!o) { setMakeCatOpen(false); setMakeCatName(""); setMakeCatTag(""); } }}>
        <DialogContent data-testid="make-category-dialog" className="max-w-sm bg-card border-border">
          <DialogHeader><DialogTitle className="font-heading text-lg">New category</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-2">Name it and add a first tag — that's what saves the category (empty ones don't stick).</p>
          <Input data-testid="make-category-name" autoFocus value={makeCatName} onChange={(e) => setMakeCatName(e.target.value)}
            placeholder="Category name (e.g. Setting, Vibe)" className="bg-secondary/50 border-border" />
          <Input data-testid="make-category-tag" value={makeCatTag} onChange={(e) => setMakeCatTag(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); createCategory(); } }}
            placeholder="First tag (e.g. city street)" className="bg-secondary/50 border-border" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMakeCatOpen(false); setMakeCatName(""); setMakeCatTag(""); }} className="rounded-full border-border">Cancel</Button>
            <Button data-testid="make-category-save" onClick={createCategory} className="rounded-full">Create category</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!newCatFor} onOpenChange={(o) => { if (!o) { setNewCatFor(null); setNewCatName(""); } }}>
        <DialogContent data-testid="new-category-dialog" className="max-w-sm bg-card border-border">
          <DialogHeader><DialogTitle className="font-heading text-lg">New category for "{newCatFor}"</DialogTitle></DialogHeader>
          <Input data-testid="new-category-input" autoFocus value={newCatName} onChange={(e) => setNewCatName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submitNewCat(); } }}
            placeholder="e.g. Vibe, Location, Mood…" className="bg-secondary/50 border-border" />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNewCatFor(null); setNewCatName(""); }} className="rounded-full border-border">Cancel</Button>
            <Button data-testid="new-category-save" onClick={submitNewCat} className="rounded-full">Create & move</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
